import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type {
  AdminMetricsResponse, CanaryMetrics, CanaryStatus,
} from '../../services/api';
import {
  RefreshCw, Loader2, Activity, Plug, Target, DollarSign, TrendingUp, ShieldAlert,
} from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

// ---- formatting helpers -----------------------------------------------------
const pct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`;
const num = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString();
const money = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`;

const STATUS_STYLES: Record<CanaryStatus, { dot: string; pill: string; label: string }> = {
  green: { dot: 'bg-green-500', pill: 'bg-green-100 text-green-700', label: 'Healthy' },
  amber: { dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-700', label: 'Watch' },
  red: { dot: 'bg-red-500', pill: 'bg-red-100 text-red-700', label: 'At Risk' },
  info: { dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600', label: 'Tracking' },
};

function StatusBadge({ status }: { status: CanaryStatus }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.info;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.pill}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function Section({
  title, premortem, icon, status, children,
}: {
  title: string; premortem: string; icon: React.ReactNode; status: CanaryStatus; children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        <StatusBadge status={status} />
      </div>
      <p className="text-sm text-gray-500 mb-4">{premortem}</p>
      {children}
    </div>
  );
}

function Stat({
  label, value, hint, tone,
}: { label: string; value: string | number; hint?: string; tone?: 'good' | 'warn' | 'bad' }) {
  const toneCls = tone === 'bad' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : tone === 'good' ? 'text-green-600' : 'text-gray-800';
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-bold ${toneCls}`}>{value}</div>
      {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function FunnelStep({ label, count, rate }: { label: string; count: number; rate?: string }) {
  return (
    <div className="flex-1 min-w-[110px] text-center">
      <div className="text-2xl font-bold text-gray-800">{num(count)}</div>
      <div className="text-xs text-gray-500">{label}</div>
      {rate && <div className="text-xs text-primary-600 font-medium mt-1">{rate}</div>}
    </div>
  );
}

export function CanaryAnalytics() {
  const [metrics, setMetrics] = useState<AdminMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setMetrics(await api.getAdminMetrics());
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load canary analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  const c: CanaryMetrics | undefined = metrics?.canaries;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Canary Analytics</h1>
          {c && <StatusBadge status={c.overall_status} />}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Early-warning signals for the five premortem failure modes (see <code>PREMORTUM.md</code>).
        Each section tracks whether a specific way SkillSnap could fail is trending in real time.
      </p>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

      {!c && !error && (
        <div className="p-4 bg-amber-50 text-amber-700 rounded-lg">
          Canary data not present in metrics response. Deploy the updated <code>admin.get_metrics</code> Lambda.
        </div>
      )}

      {c && (
        <>
          {/* 1. Ingest Health */}
          <Section
            title="1 · Ingest Health"
            premortem="The 'paste a job URL' magic moment. If new users' first URL never becomes a real card, they bounce on turn one."
            icon={<Plug className="w-5 h-5 text-blue-500" />}
            status={c.ingest.status}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
              <Stat label="Ingest success rate" value={pct(c.ingest.ingest_success_rate)}
                tone={c.ingest.status === 'red' ? 'bad' : c.ingest.status === 'amber' ? 'warn' : 'good'}
                hint="healthy ÷ total jobs" />
              <Stat label="Total jobs" value={num(c.ingest.total_jobs)} />
              <Stat label="Healthy cards" value={num(c.ingest.healthy)} />
              <Stat label="Research in progress" value={num(c.ingest.research_in_progress)} tone={c.ingest.research_in_progress ? 'warn' : undefined} />
              <Stat label="Stuck >24h" value={num(c.ingest.placeholder_stuck_24h)} tone={c.ingest.placeholder_stuck_24h ? 'bad' : 'good'}
                hint="placeholders never flipped" />
              <Stat label="Research failed" value={num(c.ingest.research_failed)} tone={c.ingest.research_failed ? 'warn' : undefined} />
              <Stat label="Placeholders" value={num(c.ingest.placeholder)} />
              <Stat label="Enrich rate-limited" value={num(c.ingest.enrich_rate_limited)} tone={c.ingest.enrich_rate_limited ? 'warn' : undefined} />
              <Stat label="Enrich failed" value={num(c.ingest.enrich_failed)} tone={c.ingest.enrich_failed ? 'warn' : undefined} />
              <Stat label="New jobs (7d)" value={num(c.ingest.new_jobs_7d)} />
            </div>
            {c.ingest.by_domain?.length > 0 && (
              <div className="overflow-x-auto">
                <div className="text-sm font-semibold text-gray-600 mb-2">Failure rate by job board (top 10)</div>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="text-left py-1.5 px-3 font-medium">Domain</th>
                      <th className="text-right py-1.5 px-3 font-medium">Jobs</th>
                      <th className="text-right py-1.5 px-3 font-medium">Failed</th>
                      <th className="text-right py-1.5 px-3 font-medium">Failure rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.ingest.by_domain.map((d) => (
                      <tr key={d.domain} className="border-b hover:bg-gray-50">
                        <td className="py-1.5 px-3 font-mono text-xs">{d.domain}</td>
                        <td className="py-1.5 px-3 text-right">{num(d.total)}</td>
                        <td className="py-1.5 px-3 text-right">{num(d.failed)}</td>
                        <td className={`py-1.5 px-3 text-right font-medium ${d.failure_rate >= 0.3 ? 'text-red-600' : d.failure_rate > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                          {pct(d.failure_rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* 2. Outcome Funnel */}
          <Section
            title="2 · Outcome Funnel"
            premortem="Do generated resumes convert to interviews? Phase progression is the real signal — not generation volume."
            icon={<Target className="w-5 h-5 text-purple-500" />}
            status={c.outcomes.status}
          >
            <div className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg p-4 mb-4 overflow-x-auto">
              <FunnelStep label="Generated" count={c.outcomes.generated} />
              <span className="text-gray-300 text-xl">→</span>
              <FunnelStep label="Applied" count={c.outcomes.applied} rate={pct(c.outcomes.ready_to_applied_rate)} />
              <span className="text-gray-300 text-xl">→</span>
              <FunnelStep label="Interviewing" count={c.outcomes.interviewing} rate={pct(c.outcomes.applied_to_interview_rate)} />
              <span className="text-gray-300 text-xl">→</span>
              <FunnelStep label="Accepted" count={c.outcomes.accepted} rate={pct(c.outcomes.interview_to_accepted_rate)} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Total user-jobs" value={num(c.outcomes.total_user_jobs)} />
              <Stat label="Generation error rate" value={pct(c.outcomes.error_rate)}
                tone={c.outcomes.status === 'red' ? 'bad' : c.outcomes.status === 'amber' ? 'warn' : 'good'}
                hint={`${num(c.outcomes.errored)} errored`} />
              <Stat label="Stalled at Ready" value={num(c.outcomes.stalled_ready)} tone={c.outcomes.stalled_ready ? 'warn' : undefined}
                hint={`${pct(c.outcomes.stalled_ready_rate)} of generated`} />
              <Stat label="PDFs generated" value={num(c.outcomes.pdf_generated)} />
            </div>
            <div className="overflow-x-auto">
              <div className="text-sm font-semibold text-gray-600 mb-2">Jobs by phase</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(c.outcomes.by_phase).sort((a, b) => b[1] - a[1]).map(([phase, count]) => (
                  <span key={phase} className="px-2.5 py-1 bg-gray-100 rounded text-sm text-gray-700">
                    {phase}: <span className="font-semibold">{num(count)}</span>
                  </span>
                ))}
              </div>
            </div>
          </Section>

          {/* 3. Economics */}
          <Section
            title="3 · Unit Economics"
            premortem="Episodic usage + per-resume LLM cost can go upside-down. Watch cost-per-active-user vs. revenue."
            icon={<DollarSign className="w-5 h-5 text-green-500" />}
            status={c.economics.status}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Active users (30d)" value={num(c.economics.active_users_30d)} hint="distinct users w/ a job in 30d" />
              <Stat label="Total AWS cost (30d)" value={money(c.economics.total_aws_cost_30d)} />
              <Stat label="Cost / active user" value={money(c.economics.cost_per_active_user)} tone="warn"
                hint="total AWS ÷ active users" />
              <Stat label="Bedrock / active user" value={money(c.economics.bedrock_per_active_user)} tone="warn" />
              <Stat label="Bedrock cost (30d)" value={money(c.economics.bedrock_cost_30d)} />
              <Stat label="Resumes started (30d)" value={num(c.economics.resumes_started_30d)} />
              <Stat label="AI calls (30d)" value={num(c.economics.ai_calls_30d)} hint={`${num(c.economics.ai_failures_30d)} failed`} />
              <Stat label="AI tokens (30d)" value={`${num(c.economics.ai_tokens_in_30d)} in`} hint={`${num(c.economics.ai_tokens_out_30d)} out`} />
            </div>
            <p className="text-xs text-gray-400 mt-3">
              No revenue baseline is wired in yet, so this section is informational. Once subscription
              revenue per active user is known, cost/active-user crossing it flips this red.
            </p>
          </Section>

          {/* 4. Conversion */}
          <Section
            title="4 · Conversion & Moat"
            premortem="Commoditized by free LLMs? Watch free→paid conversion, churn, and whether users exercise the moat features."
            icon={<TrendingUp className="w-5 h-5 text-indigo-500" />}
            status={c.conversion.status}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Real users" value={num(c.conversion.total_users)} hint="excludes admin/test" />
              <Stat label="Paid users" value={num(c.conversion.paid_users)} tone="good" />
              <Stat label="Paid conversion" value={pct(c.conversion.paid_conversion_rate)} />
              <Stat label="Trial users" value={num(c.conversion.trial_users)} />
              <Stat label="New users (30d)" value={num(c.conversion.new_users_30d)} />
              <Stat label="Cancelled (30d)" value={num(c.conversion.cancelled_30d)} tone={c.conversion.cancelled_30d ? 'warn' : undefined}
                hint={`${num(c.conversion.cancelled_users)} total`} />
              <Stat label="Moat: synonym jobs" value={num(c.conversion.moat_synonym_jobs)} hint="jobs that used synonyms" />
              <Stat label="Moat: skill-group jobs" value={num(c.conversion.moat_skill_group_jobs)} hint="jobs that used skill groups" />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-600 mb-2">Users by plan</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(c.conversion.by_plan).map(([plan, count]) => (
                  <span key={plan} className="px-2.5 py-1 bg-gray-100 rounded text-sm text-gray-700 capitalize">
                    {plan}: <span className="font-semibold">{num(count)}</span>
                  </span>
                ))}
              </div>
            </div>
          </Section>

          {/* 5. Trust */}
          <Section
            title="5 · Trust & Errors"
            premortem="A fabrication incident poisons a career-stakes product. Watch error volume, risk, and fabrication-language signals."
            icon={<ShieldAlert className="w-5 h-5 text-red-500" />}
            status={c.trust.status}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Fabrication signals" value={num(c.trust.fabrication_signals)}
                tone={c.trust.fabrication_signals_7d ? 'bad' : c.trust.fabrication_signals ? 'warn' : 'good'}
                hint={`${num(c.trust.fabrication_signals_7d)} in last 7d · Error items only`} />
              <Stat label="Open errors" value={num(c.trust.open_errors)}
                tone={c.trust.open_errors > 5 ? 'bad' : c.trust.open_errors ? 'warn' : 'good'} />
              <Stat label="High-risk open" value={num(c.trust.high_risk_open)} tone={c.trust.high_risk_open ? 'bad' : 'good'}
                hint="risk score ≥ 4" />
              <Stat label="New errors (7d)" value={num(c.trust.errors_7d)} tone={c.trust.errors_7d ? 'warn' : undefined} />
            </div>
            {Object.keys(c.trust.errors_by_source).length > 0 && (
              <div>
                <div className="text-sm font-semibold text-gray-600 mb-2">Open errors by source</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(c.trust.errors_by_source).map(([src, count]) => (
                    <span key={src} className="px-2.5 py-1 bg-gray-100 rounded text-sm text-gray-700">
                      {src}: <span className="font-semibold">{num(count)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <div className="text-xs text-gray-400 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" />
            Generated {c.generated_at ? new Date(c.generated_at).toLocaleString() : '—'}
          </div>
        </>
      )}
    </div>
  );
}
